import { Resizable } from "re-resizable";
import { act, useContext, useEffect, useRef, useState } from "react";
import "../../../../styles/Widgets/Buttons/StateNavigationButtonWidget.css";

const StateNavigationButtonWidget = ({ DashboardContext, widget, setWidgets, bounds, setDraggingOrResizing, MOBILE_COMPUTER_SCREEN_BOUNDARY}) =>{
    const {
        dashboard,
        editMode,setActiveDashboardState,
        activeEditWidget,setActiveEditWidget
    } = useContext(DashboardContext);
    //region State navigation button specific features
    const mouseDownPosition = useRef(null);
    //endregion
    //region Widget General features
    const { gridCellSize } = bounds;
    const [resizing,setResizing] = useState(false);
    const [dragging,setDragging] = useState(false);
    const [newWidgetSize,setNewWidgetSize] = useState({ width: widget.sizeX, height: widget.sizeY });
    const [widgetPosition, setWidgetPosition] = useState({ col: widget.col, row: widget.row });
    const [widgetSize, setWidgetSize] = useState({ width: widget.sizeX, height: widget.sizeY });
    const resizableRef = useRef(null);
    const mouseMoveRef = useRef(null);
    const mouseUpRef = useRef(null);
    const position = { 
        x: widgetPosition.col * (gridCellSize + bounds.colGap) + bounds.colGap,
        y: widgetPosition.row * (gridCellSize + bounds.rowGap) + bounds.rowGap
    };
    const size = { 
        width: widgetSize.width * gridCellSize,
        height: widgetSize.height * gridCellSize
    };
    const screenIsMobileScreen = () =>{
        return bounds.width < MOBILE_COMPUTER_SCREEN_BOUNDARY;
    };
    const handleMouseDown = (e) => {
        if(!editMode || resizing || screenIsMobileScreen()) return;
        if(resizableRef.current?.resizableElement?.contains(e.target)) return;

        setDragging(true);
        const startX = e.clientX - position.x;
        const startY = e.clientY - position.y;

        const handleMouseMove = (e) => {
            let newCol = Math.round((e.clientX - startX) / gridCellSize);
            let newRow = Math.round((e.clientY - startY) / gridCellSize);

            newCol = Math.max(0, Math.min(Math.floor(bounds.width / gridCellSize) - widgetSize.width, newCol));
            newRow = Math.max(0, Math.min(Math.floor(bounds.height / gridCellSize) - widgetSize.height, newRow));

            setWidgetPosition({ col: newCol, row: newRow });
        };

        const handleMouseUp = () => {
            setDragging(false);
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };

        mouseMoveRef.current = handleMouseMove;
        mouseUpRef.current = handleMouseUp;
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
    };
    const handleResizing = (e,direction,ref) =>{
        const newWidth = ref.clientWidth;
        const newHeight = ref.clientHeight;
        setNewWidgetSize({
            width:newWidth,
            height:newHeight
        });
    };
    const exportWidget = () =>{
        const blob = new Blob([JSON.stringify(widget,null,2)],{type: "application/json"});
        const url = URL.createObjectURL(blob);
        const a = Object.assign(document.createElement("a"), {href: url, download: `${widget.type}_${widget?.appearance?.config?.title}.json`});
        a.click();
        URL.revokeObjectURL(url);
    };
    const removeWidget = () =>{
        setActiveDashboardState(prev =>({
            ...prev,widgets:prev?.widgets?.filter(w => w.id !== widget?.id)
        }));
    };
    useEffect(() =>{
        setWidgetSize(prev => ({
            width: Math.round(prev.width),
            height: Math.round(prev.height),
        }));

        setWidgetPosition(prev => ({
            col: Math.round(prev.col),
            row: Math.round(prev.row),
        }));
    },[gridCellSize]);
    useEffect(() =>{
        if(screenIsMobileScreen()) return;
        setNewWidgetSize({
            width:widgetSize.width * gridCellSize,
            height:widgetSize.height * gridCellSize
        });
        setWidgets((prevWidgets) => prevWidgets.map((prevWidget) =>(
            prevWidget.id === widget.id? {
                ...prevWidget,
                row: widgetPosition.row,
                col: widgetPosition.col,
                sizeX: widgetSize.width,
                sizeY: widgetSize.height
            }: prevWidget
        )));
    },[widgetPosition,widgetSize]);
    //endregion
    return(
        <div className={`general-widget-parent-container ${editMode? (activeEditWidget?.id === widget?.id? 'selected-edit':activeEditWidget? 'active-edit':'passive-edit'):''}`}
            style={{
                position: "absolute",
                left: position.x,
                top: position.y,
                cursor: dragging ? "grabbing" : "default"
            }}
            onClick={() =>{
                if(!activeEditWidget) return;
                setActiveEditWidget(widget);
            }}
            onMouseDown={handleMouseDown}>
            <div className={`general-widget-active-edit-mask ${editMode && activeEditWidget && activeEditWidget?.id !== widget?.id? 'active':''}`}></div>
            <Resizable
                ref={resizableRef}
                size={size}
                minWidth={3 * gridCellSize}
                minHeight={1 * gridCellSize}
                enable={{ right: editMode && !activeEditWidget, bottom: editMode && !activeEditWidget, bottomRight: editMode && !activeEditWidget }}
                onResizeStart={(e) => {
                    e.stopPropagation();
                    setResizing(true);
                }}
                onResize={(e, direction, ref) =>handleResizing(e,direction,ref)}
                onResizeStop={(e,direction,ref) => {
                    setWidgetSize({
                        width: Math.round(ref.clientWidth / gridCellSize),
                        height: Math.round(ref.clientHeight / gridCellSize),
                    });
                    setResizing(false);
                }}>
                <div className="general-widget-container" style={{borderRadius:`${widget?.borderRadius}%`}} onMouseDown={handleMouseDown}>
                    <div className="general-widget-edit-controls-container"
                        style={{display:editMode && !activeEditWidget? 'flex':'none'}}>
                        <div onClick={() => setActiveEditWidget(widget)}>
                            <p className="edit-widget-p">&#9997;</p>
                        </div>
                        <div onClick={() => exportWidget()}>
                            <p className="export-widget-p">&#128229;</p>
                        </div>
                        <div onClick={() => removeWidget()}>
                            <p className="remove-widget-p">&#10006;</p>
                        </div>
                    </div>
                    <div className="state_navigation_button_widget_container"
                        title={widget?.config?.toolTip}
                        style={{
                            cursor:(widget?.config?.cursor || "").toLowerCase().replace(/_/g,' '),
                            gridTemplateColumns:`${widget?.config?.iconToTextRatio / 100}fr ${1 - (widget?.config?.iconToTextRatio / 100)}fr`
                        }}
                        onMouseDown={() =>{
                            mouseDownPosition.current = widgetPosition;
                        }}
                        onClick={() =>{
                            if (!mouseDownPosition.current) return;
                            if (
                                mouseDownPosition.current.col !== widgetPosition.col ||
                                mouseDownPosition.current.row !== widgetPosition.row
                            ) return;
                            const targetDashboardState = dashboard?.dashboardStates.find((dashboardState) => dashboardState?.id === widget?.config?.targetDashboardStateId);
                            setActiveDashboardState(targetDashboardState);
                        }}>
                            <div className="image-container">
                                <img
                                    style={{
                                        maxHeight:size?.height * 0.75,
                                        maxWidth:size?.width * widget?.config?.iconToTextRatio / 100,
                                    }}
                                    src={`${widget?.config?.icon?.imagePrefix},${widget?.config?.icon?.image}`}
                                />
                            </div>
                            <p style={{color:widget?.config?.textColor}}>{widget?.config?.text}</p>
                    </div>
                </div>
            </Resizable>
        </div>
    );
};
export default StateNavigationButtonWidget;